import { FC, useCallback, useMemo } from 'react';
import { Button, Card, RadixTable, TColumn } from '@features/ui';
import {
  PeriodUnit,
  ProductOffer,
  ReBillTimesType,
} from '@src/types/productsAddons.ts';
import { cn } from '@lib/utils.ts';
import {
  dateSelectData,
  productTypeData,
} from '@features/admin/productsAddons/productsAddonsData.ts';

interface ProductsProps {
  onEdit: (id: number) => void;
  onCopy: (id: number) => void;
  data: ProductOffer[];
}
export const Products: FC<ProductsProps> = ({ onEdit, onCopy, data }) => {
  const handleEdit = useCallback(
    (id?: number) => {
      if (!id) return;
      onEdit(id);
    },
    [onEdit]
  );
  const handleCopy = useCallback(
    (id?: number) => {
      if (!id) return;
      onCopy(id);
    },
    [onCopy]
  );

  const columns = useMemo(
    (): TColumn<ProductOffer>[] => [
      {
        title: '#',
        className: 'w-10',
        render: (_, index) => <p>{(index ?? 0) + 1}</p>,
      },
      {
        title: 'Title',
        render: (item) => <p>{item?.title}</p>,
      },
      {
        title: 'Product Categories',
        render: (item) => (
          <p>
            {productTypeData.find((it) => it.value === item?.category)?.label}
          </p>
        ),
      },
      {
        title: 'Billing Term',
        render: (item) => {
          const billingTerm = item?.billing_plan;

          const unitName = dateSelectData.find(
            (date) => date.value === billingTerm?.first_period_unit
          )?.label;

          const unitName2 = dateSelectData.find(
            (date) => date.value === billingTerm?.second_period_unit
          )?.label;

          const secondBillingTerm =
            item?.billing_plan?.rebill_times_type !==
            ReBillTimesType.NoMoreCharges
              ? `, then $${item?.billing_plan?.second_price ?? 0} for each ${item?.billing_plan?.second_period_value ?? 0} ${unitName2 ?? ''}`
              : '';

          return (
            <p>
              {billingTerm
                ? billingTerm.first_period_unit === PeriodUnit.LIFETIMES
                  ? `$${billingTerm.first_price ?? 0}`
                  : `$${billingTerm.first_price ?? 0} for each ${billingTerm.first_period_value ?? 0} ${unitName ?? ''} ${secondBillingTerm}`
                : ''}
            </p>
          );
        },
      },
      {
        title: 'Status',
        render: (item) => {
          return (
            <div
              className={cn(
                item?.is_active ? 'bg-lime' : 'bg-[#DC342E]',
                'h-[21px] pl-[10px] pr-[9px] w-fit flex items-center rounded-[2px] text-xs leading-[1.5]'
              )}
            >
              <p>{item?.is_active ? 'Active' : 'Inactive'}</p>
            </div>
          );
        },
      },
      {
        title: 'Users',
        render: (item) => <p>{item?.subscribe_users ?? 0}</p>,
      },
      {
        title: '',
        render: (item) => (
          <div className={'flex gap-x-[17px]'}>
            <Button
              className={'w-[58px]'}
              size={'sm'}
              variant={'black'}
              onClick={() => handleCopy(item?.id)}
            >
              Copy
            </Button>
            <Button
              className={'w-[58px]'}
              size={'sm'}
              variant={'secondary'}
              onClick={() => handleEdit(item?.id)}
            >
              Edit
            </Button>
          </div>
        ),
      },
    ],
    [handleCopy, handleEdit]
  );

  return (
    <Card className={'mt-[27px] pl-[18px] pr-[24px]'}>
      <RadixTable columns={columns} data={data} />
    </Card>
  );
};
